import { api } from "./api";
import {
  contentDispositionFromHeaders,
  downloadFilenameFromHeaders,
  filenameFromContentDisposition,
  readApiErrorDetail,
} from "./portalFiles";

export async function sendPacket({ fileId, invoiceId, toEmail } = {}) {
  const body = {};
  if (fileId) body.file_id = fileId;
  if (invoiceId) body.invoice_id = invoiceId;
  if (toEmail) body.to_email = toEmail;
  const { data } = await api.post("/admin/packets/send", body);
  return data;
}

async function downloadBlobFromUrl(url, fallbackName) {
  try {
    const { data, headers } = await api.get(url, {
      responseType: "blob",
      // PDF gen/stream can exceed the default axios timeout through the Vercel proxy.
      timeout: 120000,
    });
    if (data instanceof Blob && data.type && data.type.includes("json")) {
      const parsed = JSON.parse(await data.text());
      const err = new Error(parsed?.detail || "Download failed");
      err.response = { data: parsed };
      throw err;
    }
    // Empty/non-PDF blobs usually mean a proxy/HTML error page.
    if (data instanceof Blob && data.size < 64) {
      const head = (await data.slice(0, 64).text()).trim();
      if (head.startsWith("{") || head.startsWith("<")) {
        throw Object.assign(new Error("Download failed — empty or invalid PDF response"), {
          response: { data: { detail: "Download failed — empty or invalid PDF response" } },
        });
      }
    }
    const name =
      downloadFilenameFromHeaders(headers, fallbackName) ||
      filenameFromContentDisposition(contentDispositionFromHeaders(headers)) ||
      fallbackName ||
      "document.pdf";
    const blobUrl = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(blobUrl);
    return { filename: name };
  } catch (err) {
    const detail = await readApiErrorDetail(err);
    if (detail) {
      const wrapped = new Error(typeof detail === "string" ? detail : "Download failed");
      wrapped.response = { data: { detail } };
      throw wrapped;
    }
    if (err?.code === "ECONNABORTED" || /timeout/i.test(String(err?.message || ""))) {
      const wrapped = new Error("Download timed out — try again in a moment");
      wrapped.response = { data: { detail: wrapped.message } };
      throw wrapped;
    }
    if (/network error/i.test(String(err?.message || ""))) {
      const wrapped = new Error(
        "Could not reach the server for this PDF. Refresh and try Download again.",
      );
      wrapped.response = { data: { detail: wrapped.message } };
      throw wrapped;
    }
    throw err;
  }
}

export async function downloadPacketByFileId(fileId, fallbackName = "document.pdf") {
  return downloadBlobFromUrl(
    `/admin/packets/${encodeURIComponent(fileId)}/download`,
    fallbackName,
  );
}

export async function downloadPacketByInvoiceId(invoiceId, fallbackName = "invoice.pdf") {
  if (!invoiceId) {
    const err = new Error("Invoice id is required for PDF download");
    err.response = { data: { detail: err.message } };
    throw err;
  }
  return downloadBlobFromUrl(
    `/admin/revenue/invoices/${encodeURIComponent(invoiceId)}/packet-download`,
    fallbackName,
  );
}
